import {
    inject,
    DestroyRef,
} from '@angular/core'

import {
    Observable,
    Observer,
    Subscription
} from 'rxjs'

/**
 * Subscribes to an observable and automatically unsubscribes when the current 
 * injection context (component, directive, or service) is destroyed.
 * * @important 
 * This function MUST be called within an injection context:
 * - Directly as a class property initialization.
 * - Within the constructor.
 * - Within a runInInjectionContext execution block.
 *
 * @template T - The type of the observable value.
 * @param {Observable<T>} obs$ - The observable to subscribe to.
 * @param {Partial<Observer<T>> | ((value: T) => void)} observerOrNext - The observer object or next callback.
 * @returns {Subscription} The RxJS subscription object.
 */
export function subscribeInComponent<T>(obs$: Observable<T>, observerOrNext: Partial<Observer<T>> | ((value: T) => void)): Subscription {
    // Inject DestroyRef from the current context
    const destroyRef = inject(DestroyRef);

    // Execute the subscription
    const subscription = obs$.subscribe(observerOrNext as any);

    // Register the unsubscription callback
    const unregisterDestroy = destroyRef.onDestroy(() => {
        subscription.unsubscribe();
    });

    // Cleanup the DestroyRef callback if the subscription completes/errors 
    // before the component is destroyed to prevent memory overhead.
    subscription.add(() => unregisterDestroy());

    return subscription;
}